import { jsx } from '@amazon/vinyl-tsx'
import { type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import { type TrackFields } from '@/track/trackFieldsFrag'
import { getTrackAlbumTitle, getTrackArtistStr } from '@/track/trackGqlUtil'
import { formatNumber, formatSecondsToHms } from '@/util/string'
import { getLargestImage } from '@/util/imageUtil'
import { createImageModel } from '@/util/ImageUrl'
import { ImageSizes } from '@/style/styleCommon'
import { ImagePlaceholder } from '@/components/ImagePlaceholder'
import { type ImageModel, ImageView } from '@/components/ImageView'
import { Icon } from '@/components/Icon'
import {
    createPlayQueueBinding,
    PlayQueueState,
    type PlayQueueBindingDeps,
} from '@/playback/playQueueBinding'
import { TrackLikeButton } from '@/track/TrackLikeButton'
import { TrackOverflowButton } from '@/track/TrackOverflowButton'
import { logWarn, type Maybe } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'

export type TrackRendererDeps = PlayQueueBindingDeps

export interface TrackRendererProps {
    /**
     * The visual index, 1-based
     */
    readonly index: ObservableValue<number>
    readonly track: MutableValue<Maybe<TrackFields>>
    readonly deps: TrackRendererDeps
    /**
     * When true, suppresses the inline album column (and the
     * tap-to-play-album affordance that comes with it). Album-detail
     * pages set this — every row already belongs to the album in the
     * header, so showing it again on each track is noise. Mirrors GH's
     * AlbumDetailPage track row, which omits the album field too.
     */
    readonly hideAlbum?: boolean
}

export function TrackRenderer(props: TrackRendererProps) {
    const { target } = ctx('TrackRenderer')
    const { index, track } = props
    const imageModel = track.pick('images').map((images): ImageModel => {
        const image = getLargestImage(images, 'PRIMARY') ?? getLargestImage(images)
        return createImageModel(image, {
            scaleToSquare: ImageSizes.SMALL,
        })
    })

    const placeholder = track.map((v) => (v == null ? 'placeholder' : null))

    const playQueue = createPlayQueueBinding({
        playParams: track.pick('playParams'),
        deps: props.deps,
    })

    const playQueueEntityState = playQueue.playableEntityState(track)

    function play() {
        const t = track.value
        if (!t) {
            logWarn(target, 'play(), no track set')
            return
        }
        // Overlay doubles as pause when this row is the currently-playing
        // track. Any other state (paused, or a different queue live)
        // routes through playEntity, which sets the queue or resumes.
        if (playQueueEntityState.value === PlayQueueState.PLAYING) {
            playQueue.togglePlay()
        } else {
            playQueue.playEntity(t)
        }
    }

    const isCurrentlyPlaying = playQueueEntityState.map((s) => s === PlayQueueState.PLAYING)

    // Tapping the album cell plays the album (creates a queue from
    // album playParams and starts it). Mirrors hornet's "tap-to-play"
    // affordance on the album column. Falls back gracefully if the
    // album playParams are missing — usually a non-catalog track.
    const albumPlayQueue = createPlayQueueBinding({
        playParams: track.map((t) => t?.album?.playParams ?? null),
        deps: props.deps,
    })
    function playAlbum() {
        if (track.value?.album?.playParams) albumPlayQueue.togglePlay()
    }

    return (
        <div
            className="trackRenderer"
            classList={[playQueueEntityState, index.map((v) => (v % 2 === 0 ? 'even' : 'odd'))]}
            onclick={(event) => {
                const btn = (event.target as HTMLElement).closest('button')
                if (btn) return // A sub-button was clicked, ignore
                play()
            }}
        >
            <div className="trackIndex">
                <span className="trackIndexNumber">
                    {index.map((index) => formatNumber(index + 1))}
                </span>
                {/*
                  Equalizer bars revealed when this row's playable
                  state is `playing` (or `paused`); animation pauses
                  while paused. Three plain divs animated via CSS so
                  there's no Lottie / runtime JS animation cost.
                */}
                <span className="trackIndexEqualizer" aria-hidden="true">
                    <span className="bar" />
                    <span className="bar" />
                    <span className="bar" />
                </span>
            </div>
            <button
                className="imageButton"
                onclick={play}
                aria-label={isCurrentlyPlaying.map((p) => (p ? 'Pause' : 'Play'))}
            >
                <ImageView
                    image={imageModel}
                    placeholder={<ImagePlaceholder />}
                    loadWhenIdle={false}
                    loadImmediately={true}
                />
                <span className="trackPlayOverlay" aria-hidden="true">
                    <Icon icon={isCurrentlyPlaying.map((p) => (p ? 'pause' : 'play'))} />
                </span>
            </button>
            <div className="trackInfo">
                <div className="trackTitle" classList={[placeholder]}>
                    {track.pick('title')}
                </div>
                <div className="trackArtist" classList={[placeholder]}>
                    {track.map(getTrackArtistStr)}
                </div>
            </div>
            {props.hideAlbum ? null : (
                <button
                    className="trackAlbum trackAlbumBtn textLink"
                    classList={[placeholder]}
                    disabled={track.map((t) => t?.album?.playParams == null)}
                    onclick={(event) => {
                        event.stopPropagation()
                        playAlbum()
                    }}
                    aria-label={track.map((t) =>
                        t?.album?.title ? `Play album: ${t.album.title}` : 'Play album'
                    )}
                >
                    {track.map(getTrackAlbumTitle)}
                </button>
            )}
            <div className="trackDuration" classList={[placeholder]}>
                {track.pick('duration').map(formatSecondsToHms)}
            </div>
            <div className="trackActions">
                <TrackLikeButton data={track} />
                <TrackOverflowButton data={track} deps={props.deps} />
            </div>
        </div>
    )
}
